import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, CreditCard, MessageCircle, IndianRupee, ArrowLeft, Lock, Eye, EyeOff,
  TrendingUp, ShoppingCart, AlertCircle, Activity, Search, ChevronDown, ChevronUp,
  X, Phone, Calendar, User, HelpCircle, Clock, Star, ArrowUpRight, BarChart3, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { API_BASE as API } from "@/config";
import axios from "axios";
import AdminAffiliatePanel from "@/components/AdminAffiliatePanel";
import { getAdminToken, setAdminToken, onAdminSessionExpired } from "@/lib/adminApi";

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

function StatCard({ icon, label, value, subtext, color, trend }) {
  return (
    <motion.div variants={fadeIn}>
      <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
              {icon}
            </div>
            {trend !== undefined && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${trend >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                {trend >= 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>{value}</p>
          <p className="text-gray-500 text-xs mt-1">{label}</p>
          {subtext && <p className="text-gray-400 text-[11px] mt-0.5">{subtext}</p>}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function MiniBarChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="revenue-chart">
      <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>Revenue Trend (14 Days)</h3>
      <div className="flex items-end gap-1.5 h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div className="w-full rounded-t-md bg-gradient-to-t from-amber-400 to-amber-300 transition-all duration-300 hover:from-amber-500 hover:to-amber-400 cursor-pointer"
              style={{ height: `${Math.max(4, (d.revenue / maxRevenue) * 100)}%`, minHeight: '4px' }}>
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                ₹{d.revenue} · {d.orders} orders
              </div>
            </div>
            <span className="text-[9px] text-gray-400 truncate w-full text-center">{d.date?.split(' ')[1]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerDetail({ customer, onClose }) {
  if (!customer) return null;
  const c = customer.customer;
  const payments = customer.payments || [];
  const chat = customer.chat_history || [];
  const report = customer.report;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-8 px-4 overflow-y-auto"
      onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mb-8 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-amber-500 px-6 py-5 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{c?.name}</h3>
            <p className="text-white/80 text-xs">{c?.session_id?.slice(0, 16)}...</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { icon: <Phone className="w-4 h-4" />, label: "Phone", value: c?.phone || "N/A" },
              { icon: <Calendar className="w-4 h-4" />, label: "DOB", value: c?.birth_date || "N/A" },
              { icon: <User className="w-4 h-4" />, label: "Gender", value: c?.gender || "N/A" },
              { icon: <HelpCircle className="w-4 h-4" />, label: "Question", value: c?.question?.slice(0, 40) || "N/A" },
              { icon: <CreditCard className="w-4 h-4" />, label: "Plan", value: c?.current_plan === "premium" ? "Premium (₹99)" : c?.payment_status === "paid" ? "Basic (₹49)" : "Unpaid" },
              { icon: <IndianRupee className="w-4 h-4" />, label: "Total Paid", value: `₹${(c?.total_paid || 0) / 100}` },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-gray-400 mb-1">{item.icon}<span className="text-[10px] uppercase tracking-wider">{item.label}</span></div>
                <p className="text-sm font-semibold text-gray-900 truncate">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Report Summary */}
          {report && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3">Report Summary</h4>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                  { label: "Love", value: report.love_score, color: "#FF4D6A" },
                  { label: "Career", value: report.career_score, color: "#4CAF50" },
                  { label: "Wealth", value: report.wealth_score, color: "#FFC107" },
                  { label: "Marriage", value: report.marriage_score, color: "#E91E63" },
                  { label: "Health", value: report.health_score, color: "#00BCD4" },
                  { label: "Personality", value: report.personality_score, color: "#2196F3" },
                ].map((s, i) => (
                  <div key={i} className="text-center bg-gray-50 rounded-xl p-2">
                    <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}%</p>
                    <p className="text-[10px] text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment History */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-3">Payment History ({payments.length})</h4>
            {payments.length > 0 ? (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {payments.map((p, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                    <div>
                      <p className="text-xs font-mono text-gray-600">{p.order_id?.slice(0, 24)}...</p>
                      <p className="text-[10px] text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleString() : "-"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">₹{(p.amount || 0) / 100}</p>
                      <span className={`text-[10px] font-semibold ${p.status === "paid" ? "text-green-500" : "text-yellow-500"}`}>{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-gray-400">No payments</p>}
          </div>

          {/* Chat History */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-3">Chat History ({chat.length} messages)</h4>
            {chat.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto bg-gray-50 rounded-xl p-3">
                {chat.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`rounded-xl px-3 py-2 text-xs max-w-[80%] ${msg.role === "user" ? "bg-amber-100 text-gray-800" : "bg-white text-gray-700 border border-gray-200"}`}>
                      {msg.content?.slice(0, 300)}{msg.content?.length > 300 ? "..." : ""}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-gray-400">No messages</p>}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(() => !!getAdminToken());

  useEffect(() => {
    onAdminSessionExpired(() => { setAuthenticated(false); toast.error("Admin session expired. Please log in again."); });
    return () => onAdminSessionExpired(() => {});
  }, []);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.status === "success" && res.data.token) { setAdminToken(res.data.token); setAuthenticated(true); toast.success("Admin access granted"); }
    } catch (err) { toast.error(err.response?.status === 429 ? err.response.data.detail : "Invalid password"); }
    finally { setLoading(false); }
  };

  const fetchDashboard = useCallback(async () => {
    if (!authenticated) return;
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setDashboard(res.data);
    } catch { console.error("Dashboard fetch failed"); }
  }, [authenticated]);

  const fetchCustomers = useCallback(async () => {
    if (!authenticated) return;
    try {
      const res = await axios.get(`${API}/admin/customers`, {
        params: { search: searchQuery, status: statusFilter, sort_by: sortBy, sort_order: sortOrder, page: currentPage, limit: 30 }
      });
      setCustomers(res.data.customers || []);
      setCustomerTotal(res.data.total || 0);
    } catch { console.error("Customers fetch failed"); }
  }, [authenticated, searchQuery, statusFilter, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    if (!authenticated) return;
    setDashLoading(true);
    Promise.all([fetchDashboard(), fetchCustomers()]).finally(() => setDashLoading(false));
    const interval = setInterval(() => { fetchDashboard(); fetchCustomers(); }, 30000);
    return () => clearInterval(interval);
  }, [authenticated, fetchDashboard, fetchCustomers]);

  const openCustomerDetail = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/admin/customers/${sessionId}`);
      setSelectedCustomer(res.data);
    } catch { toast.error("Failed to load customer details"); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortOrder("desc"); }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-6" data-testid="admin-login-page">
        <motion.div initial="hidden" animate="visible" variants={fadeIn} className="w-full max-w-sm">
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Lock className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Admin Panel</h1>
              <p className="text-gray-500 text-sm mt-2">Enter password to access dashboard</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input data-testid="admin-password-input" type={showPassword ? "text" : "password"} placeholder="Enter admin password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-50 border-gray-200 focus:border-amber-400 text-gray-900 h-12 rounded-xl pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button data-testid="admin-login-btn" type="submit" disabled={loading || !password}
                className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-white hover:from-amber-500 hover:to-amber-700 font-semibold rounded-xl h-12 shadow-md">
                {loading ? "Verifying..." : "Access Dashboard"}
              </Button>
            </form>
            <Button variant="ghost" onClick={() => navigate("/")} className="w-full mt-4 text-gray-400 hover:text-gray-700">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (dashLoading && !dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-gray-100 px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro Admin</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs hidden sm:block">Auto-refresh: 30s</span>
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {["overview", "customers", "affiliates"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all capitalize ${activeTab === tab ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid={`tab-${tab}`}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {activeTab === "overview" && (
          <>
            {/* Stats Grid */}
            <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard icon={<Users className="w-5 h-5 text-blue-500" />} label="Total Users" value={dashboard?.total_users || 0} color="bg-blue-50" />
              <StatCard icon={<ShoppingCart className="w-5 h-5 text-purple-500" />} label="Total Orders" value={dashboard?.total_orders || 0} color="bg-purple-50" />
              <StatCard icon={<CreditCard className="w-5 h-5 text-green-500" />} label="Successful Payments" value={dashboard?.successful_payments || 0} color="bg-green-50" />
              <StatCard icon={<AlertCircle className="w-5 h-5 text-red-400" />} label="Failed Payments" value={dashboard?.failed_payments || 0} color="bg-red-50" />
              <StatCard icon={<IndianRupee className="w-5 h-5 text-amber-600" />} label="Today's Revenue" value={`₹${dashboard?.today_revenue || 0}`} color="bg-amber-50" />
              <StatCard icon={<IndianRupee className="w-5 h-5 text-amber-600" />} label="Monthly Revenue" value={`₹${dashboard?.monthly_revenue || 0}`} color="bg-amber-50" />
              <StatCard icon={<IndianRupee className="w-5 h-5 text-amber-600" />} label="Total Revenue" value={`₹${dashboard?.total_revenue || 0}`} color="bg-amber-50" />
              <StatCard icon={<Activity className="w-5 h-5 text-emerald-500" />} label="Active Chats" value={dashboard?.active_chats || 0} color="bg-emerald-50" />
            </motion.div>

            {/* Analytics Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{dashboard?.conversion_rate || 0}%</p>
                <p className="text-xs text-gray-500 mt-1">Conversion Rate</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{dashboard?.upgrade_rate || 0}%</p>
                <p className="text-xs text-gray-500 mt-1">Upgrade Rate</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>₹{dashboard?.avg_order_value || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Avg Order Value</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{dashboard?.today_orders || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Today's Orders</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{dashboard?.month_orders || 0}</p>
                <p className="text-xs text-gray-500 mt-1">This Month</p>
              </div>
            </div>

            {/* Revenue Chart */}
            <MiniBarChart data={dashboard?.daily_revenue} />
          </>
        )}

        {activeTab === "customers" && (
          <>
            {/* Search & Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input data-testid="customer-search" placeholder="Search by name, phone, or ID..." value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-10 bg-white border-gray-200 rounded-xl h-11" />
              </div>
              <div className="flex gap-2">
                {["", "paid", "pending", "active"].map(f => (
                  <button key={f} onClick={() => { setStatusFilter(f); setCurrentPage(1); }}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all capitalize ${statusFilter === f ? "bg-amber-100 text-amber-700" : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50"}`}
                    data-testid={`filter-${f || 'all'}`}>
                    {f || "All"}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-xs text-gray-400">{customerTotal} customers found</p>

            {/* Customer Table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="customer-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      {[
                        { key: "name", label: "Name" },
                        { key: null, label: "Phone" },
                        { key: null, label: "DOB" },
                        { key: null, label: "Gender" },
                        { key: null, label: "Question" },
                        { key: "total_paid", label: "Paid" },
                        { key: "payment_status", label: "Status" },
                        { key: null, label: "Plan" },
                        { key: null, label: "Upgrade" },
                        { key: "created_at", label: "Date" },
                      ].map((col, i) => (
                        <th key={i} className="text-left px-3 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                          {col.key ? (
                            <button onClick={() => toggleSort(col.key)} className="flex items-center gap-1 hover:text-gray-900 transition-colors">
                              {col.label}
                              {sortBy === col.key && (sortOrder === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                            </button>
                          ) : col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length > 0 ? customers.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-amber-50/30 cursor-pointer transition-colors"
                        onClick={() => openCustomerDetail(c.session_id)} data-testid={`customer-row-${i}`}>
                        <td className="px-3 py-3 font-semibold text-gray-900 whitespace-nowrap">{c.name}</td>
                        <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{c.phone || "-"}</td>
                        <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{c.birth_date}</td>
                        <td className="px-3 py-3 text-gray-600 text-xs capitalize">{c.gender}</td>
                        <td className="px-3 py-3 text-gray-500 text-xs max-w-[150px] truncate">{c.question}</td>
                        <td className="px-3 py-3 font-semibold text-gray-900 text-xs whitespace-nowrap">₹{c.total_paid_display || 0}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            c.chat_active ? "bg-green-50 text-green-600" : c.payment_status === "paid" ? "bg-blue-50 text-blue-600" : "bg-yellow-50 text-yellow-600"
                          }`}>
                            {c.chat_active ? "Active" : c.payment_status === "paid" ? "Paid" : "Pending"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-xs capitalize">{c.current_plan || "none"}</td>
                        <td className="px-3 py-3">
                          {c.upgrade_purchased ? (
                            <span className="text-green-500 text-[10px] font-bold">Yes</span>
                          ) : (
                            <span className="text-gray-300 text-[10px]">No</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={10} className="text-center text-gray-400 py-12">No customers found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {customerTotal > 30 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Page {currentPage} of {Math.ceil(customerTotal / 30)}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}
                      className="text-xs rounded-lg">Previous</Button>
                    <Button variant="outline" size="sm" disabled={currentPage >= Math.ceil(customerTotal / 30)} onClick={() => setCurrentPage(p => p + 1)}
                      className="text-xs rounded-lg">Next</Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "affiliates" && <AdminAffiliatePanel />}
      </main>

      {/* Customer Detail Modal */}
      <AnimatePresence>
        {selectedCustomer && <CustomerDetail customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} />}
      </AnimatePresence>
    </div>
  );
}
